import type { InfluencerData } from "@/features/influencer-monitoring/store/influencerStore";
import { URL_PATTERNS, URL_TEMPLATES } from "@/constants/platformData";

/**
 * Checks if a string looks like a URL
 */
export const isUrl = (str: string): boolean => {
  return (
    str.includes("http://") ||
    str.includes("https://") ||
    str.includes("www.") ||
    str.includes(".com")
  );
};

/**
 * Extracts username/handle from social media URLs
 */
export const extractUsername = (
  url: string,
  platform: keyof InfluencerData,
): string => {
  if (!url || url === "N/A") return url;

  // Special handling for Reddit URLs
  if (platform === "reddit") {
    const redditMatch = url.match(/(?:reddit\.com\/r\/)([^/?#]+)/i);
    if (redditMatch) return redditMatch[1];
  }

  try {
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    const pathname = urlObj.pathname;

    // Get the first part of the path and clean it up
    const username = pathname.replace(/^\/+/, "").split("/")[0];

    // ✅ Remove query, fragments, and optional leading "@"
    return username.split("?")[0].split("#")[0].replace(/^@/, "");
  } catch {
    // Fallback regex
    const pattern = URL_PATTERNS[platform];
    if (pattern) {
      const match = url.match(pattern);
      if (match) return match[1].replace(/^@/, ""); // ✅ Also sanitize here
    }

    return url;
  }
};

/**
 * Constructs full URL from username/handle
 */
export const constructUrl = (
  username: string,
  platform: keyof InfluencerData,
): string => {
  const template = URL_TEMPLATES[platform];
  return template.replace("{username}", username);
};

/**
 * Extracts platform and username from a social media URL
 * Returns null if the platform cannot be determined
 */
export const extractPlatformAndUsernameFromUrl = (
  url: string,
): {
  platform: "yt" | "reddit" | "tiktok" | "instagram" | "facebook" | "x";
  username: string;
} | null => {
  if (!url || !isUrl(url)) return null;

  // Map of platform patterns to platform names (using internal names)
  const platformPatterns: Array<{
    name: "yt" | "reddit" | "tiktok" | "instagram" | "facebook" | "x";
    pattern: RegExp;
  }> = [
    { name: "facebook", pattern: /(?:facebook\.com\/|fb\.com\/)([^/?#]+)/i },
    { name: "x", pattern: /(?:x\.com\/)([^/?#]+)/i },
    { name: "x", pattern: /(?:twitter\.com\/)([^/?#]+)/i }, // Twitter URLs map to "x"
    { name: "instagram", pattern: /(?:instagram\.com\/|ig\.com\/)([^/?#]+)/i },
    { name: "reddit", pattern: /(?:reddit\.com\/r\/)([^/?#]+)/i },
    {
      name: "yt",
      pattern:
        /(?:youtube\.com\/channel\/|youtube\.com\/c\/|youtube\.com\/@)([^/?#]+)/i,
    },
    { name: "tiktok", pattern: /(?:tiktok\.com\/@)([^/?#]+)/i },
  ];

  for (const { name, pattern } of platformPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: name,
        username: match[1].replace(/^@/, ""),
      };
    }
  }

  return null;
};
